/**
 * Spec: https://docs.google.com/document/d/1zDn7RBMeJuoqcGtGRZtIM17fxAj12EYEEI5U8RVQENQ/edit#heading=h.anwrirsd22my
 */
import { defer } from 'lodash'

import { track as mcTrack } from '@mc/track-event'
import { paths } from '@mc/paths'
import {
  ActionTypes,
  KeyTypes,
  saveUserData,
  getTrackedId,
} from '@mc/user-data'
import {
  getPageEventName,
  type PageEventNameType,
} from '@mc/pre-paywall-events'

import { CourseSubType } from 'types/data'

import i18nInstance from 'i18n/i18nInstance'

export const LOCATION_RELATED_COURSES = 'related-courses'

type ArrowDirection = 'left' | 'right'

type TrackingProps = {
  action: string
  location: string
  class?: string
  category?: string
  subcategory?: string
  link?: string
  stored_category_id?: string
  position_index?: number
  lesson?: string
}

const track = (event: PageEventNameType, props: TrackingProps) =>
  defer(() => mcTrack(event, props))

// If User Clicks on a Category
export const trackCategoryClick = (slug: string, id?: string) => {
  const eventName = getPageEventName()
  let catId = ''

  if (id) {
    catId = getTrackedId({ key: KeyTypes.CATEGORY, id })
  }

  track(eventName, {
    action: slug,
    location: LOCATION_RELATED_COURSES,
    stored_category_id: catId,
  })

  if (window.location.pathname === paths.root) {
    saveUserData({
      key: KeyTypes.CATEGORY,
      id: catId,
      actionType: ActionTypes.BROWSING,
      eventName,
    })
  }
}

// If User Clicks on a Tile
export const trackTileClick = ({
  classTitle,
  carouselSlug,
  location,
  link,
  ...trackProps
}: {
  classTitle: string
  carouselSlug: string
  location: string
  link: string
}) => {
  const eventName = getPageEventName()
  track(eventName, {
    action: 'click',
    category: carouselSlug,
    class: classTitle,
    location,
    link,
    ...trackProps,
  })
}

// If User Clicks on left/right arrows
const trackCarouselArrowClick = ({
  arrow,
  carouselSlug,
  location,
}: {
  arrow: ArrowDirection
  carouselSlug: string
  location: string
}) => {
  const eventName = getPageEventName()
  track(eventName, {
    action: arrow,
    category: carouselSlug,
    location,
  })
}

export const trackCarouselClickPrev = ({
  carouselSlug,
  location,
}: {
  carouselSlug: string
  location: string
}) => {
  trackCarouselArrowClick({ arrow: 'left', carouselSlug, location })
}

export const trackCarouselClickNext = ({
  carouselSlug,
  location,
}: {
  carouselSlug: string
  location: string
}) => {
  trackCarouselArrowClick({ arrow: 'right', carouselSlug, location })
}

// If user Clicks on the "Watch Trailer" button
export const trackWatchTrailerClick = ({
  location = 'body',
  slug,
  type,
  lessonTitle,
}: {
  slug: string
  location: string
  type: CourseSubType
  lessonTitle?: string
}) => {
  track(getPageEventName(), {
    action: i18nInstance.t('home.trendingCarousel.watchTrailer', {
      ns: '@mc/next',
    }),
    location,
    class: slug,
    link: paths.courseTypePage(type, slug),
    category: 'class-catalog',
    lesson: lessonTitle,
  })
}

export const trackInstructorTileClicked = ({
  colIdx,
  tileClass,
  categoryName,
  subcatName,
}: {
  colIdx: number
  tileClass: string
  categoryName: string
  subcatName?: string
}) => {
  const eventName = getPageEventName()
  track(eventName, {
    location: 'meet_instructors',
    action: 'click', // TODO
    class: tileClass,
    position_index: colIdx,
    category: categoryName,
    subcategory: subcatName,
  })
}
